import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useSEO from '../../hooks/useSEO';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { publicAPI } from '../../lib/api';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';
import {
  Building2,
  Users,
  Shield,
  CreditCard,
  FileText,
  Bell,
  TrendingUp,
  CheckCircle,
  Star,
  MapPin,
  ArrowRight,
  Home,
  UserCheck,
  Wallet,
  HeadphonesIcon,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Play,
  ShieldCheck,
  Gift
} from 'lucide-react';

// Sample fallback properties (used if no properties in database)
const FALLBACK_PROPERTIES = [
  {
    property_id: 1,
    name: "Sunrise Dormitory",
    city: "Quezon City",
    province: "Metro Manila",
    min_price: 3500,
    max_price: 5500,
    image: "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=600&h=400&fit=crop",
    total_units: 24,
    available_units: 5,
    amenities: ["WiFi", "AC", "Laundry", "Security"]
  },
  {
    property_id: 2,
    name: "Green Valley Boarding House",
    city: "Makati City",
    province: "Metro Manila",
    min_price: 4000,
    max_price: 6500,
    image: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600&h=400&fit=crop",
    total_units: 18,
    available_units: 3,
    amenities: ["WiFi", "Kitchen", "Parking", "CCTV"]
  },
  {
    property_id: 3,
    name: "University Heights Dorm",
    city: "Los Baños",
    province: "Laguna",
    min_price: 2800,
    max_price: 4200,
    image: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=600&h=400&fit=crop",
    total_units: 32,
    available_units: 8,
    amenities: ["WiFi", "Study Room", "Canteen", "Guard"]
  },
  {
    property_id: 4,
    name: "Metro Central Residences",
    city: "Cebu City",
    province: "Cebu",
    min_price: 3200,
    max_price: 5000,
    image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600&h=400&fit=crop",
    total_units: 40,
    available_units: 12,
    amenities: ["WiFi", "Pool", "Gym", "Elevator"]
  }
];

// Fallback testimonials
const FALLBACK_TESTIMONIALS = [
  {
    id: 1,
    name: "Maria Santos",
    role: "Landlord",
    location: "Quezon City",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
    text: "U-DORM transformed how I manage my 3 boarding houses. Payment tracking used to be a nightmare, now it's automated. My tenants love the easy proof-of-payment system!",
    rating: 5
  },
  {
    id: 2,
    name: "Juan Dela Cruz",
    role: "Tenant",
    location: "Makati City",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop",
    text: "Finding a dorm near my university was so easy with U-DORM. The lease agreement is digital, payments are tracked, and I can message my landlord directly in the app.",
    rating: 5
  },
  {
    id: 3,
    name: "Ana Reyes",
    role: "Property Manager",
    location: "Cebu City",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop",
    text: "Managing 5 properties for different landlords is now a breeze. The analytics dashboard helps me report to property owners with professional-looking reports.",
    rating: 5
  },
  {
    id: 4,
    name: "Roberto Lim",
    role: "Landlord",
    location: "Davao City",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop",
    text: "The subscription is worth every peso! I upgraded to Pro and now I can manage all my units across 3 locations. Customer support is also very responsive.",
    rating: 5
  }
];

const LANDLORD_BENEFITS = [
  {
    icon: Building2,
    title: "Multi-Property Management",
    description: "Manage unlimited properties and units from a single dashboard"
  },
  {
    icon: CreditCard,
    title: "Payment Tracking",
    description: "Track rent payments, verify proofs, and generate receipts automatically"
  },
  {
    icon: FileText,
    title: "Digital Lease Agreements",
    description: "Create, sign, and manage lease contracts digitally with PDF export"
  },
  {
    icon: Users,
    title: "Tenant Management",
    description: "Onboard tenants easily, track their status, and manage their lifecycle"
  },
  {
    icon: TrendingUp,
    title: "Analytics & Reports",
    description: "Get insights on occupancy rates, revenue trends, and financial reports"
  },
  {
    icon: Bell,
    title: "Automated Reminders",
    description: "Send payment reminders and late notices automatically via email"
  }
];

const TENANT_BENEFITS = [
  {
    icon: Home,
    title: "Easy Room Search",
    description: "Browse available dorms and boarding houses in your area"
  },
  {
    icon: Shield,
    title: "Secure Transactions",
    description: "Upload payment proofs and get verified receipts for your records"
  },
  {
    icon: FileText,
    title: "Digital Contracts",
    description: "Sign lease agreements digitally and access them anytime"
  },
  {
    icon: Bell,
    title: "Payment Reminders",
    description: "Never miss a due date with automated payment notifications"
  },
  {
    icon: HeadphonesIcon,
    title: "Direct Communication",
    description: "Message your landlord directly through the app"
  },
  {
    icon: CheckCircle,
    title: "Transparent History",
    description: "View your complete payment history and lease details"
  }
];

export default function LandingPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);
  const [stats, setStats] = useState({ total_properties: 500, total_tenants: 2000, total_landlords: 150 });
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [plans, setPlans] = useState(null);

  // Per-page SEO — route-aware so /pricing, /features, etc. each get tailored social cards
  const { pathname } = useLocation();
  const SEO_BY_PATH = {
    '/pricing': {
      title: 'Pricing — One Plan, $9.99/mo or $95/yr · U-DORM',
      description: 'Simple, transparent pricing. $9.99 per month or $95 per year (save ~20%). No auto-renew. 30-Day Money-Back Guarantee. Unlimited properties, units & tenants.',
    },
    '/features': {
      title: 'Features — Manage Properties, Leases, Payments & More · U-DORM',
      description: 'Everything you need to run your dorm: property & unit management, lease workflows, payment tracking with PDF receipts, in-app messaging, and reports.',
    },
    '/contact': {
      title: 'Contact Us · U-DORM',
      description: 'Get in touch via Messenger or email info@mindfulux.org for a free demo, sales questions, or support. We typically respond within hours.',
    },
    '/testimonials': {
      title: 'What Our Landlords & Tenants Say · U-DORM',
      description: 'Hear from real landlords and tenants who use U-DORM to simplify their rental experience.',
    },
  };
  const pageSEO = SEO_BY_PATH[pathname] || {
    title: 'U-DORM — Dormitory Management Made Simple',
    description: 'One plan, unlimited everything: $9.99/month or $95/year. No auto-renew. 30-Day Money-Back Guarantee. Manage properties, leases, tenants & payments in one place.',
  };
  useSEO({ ...pageSEO, image: '/logo.png' });

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch properties and testimonials in parallel
        const [propertiesRes, testimonialsRes, plansRes] = await Promise.all([
          publicAPI.getProperties(8).catch(() => null),
          publicAPI.getTestimonials().catch(() => null),
          publicAPI.getPlans().catch(() => null)
        ]);

        if (plansRes?.data) setPlans(plansRes.data);

        // Set properties
        if (propertiesRes?.data?.properties?.length > 0) {
          setProperties(propertiesRes.data.properties);
          // Update stats from API
          if (propertiesRes.data.stats) {
            setStats(prev => ({
              ...prev,
              total_properties: Math.max(propertiesRes.data.stats.total_properties, 100),
              total_tenants: Math.max(propertiesRes.data.stats.total_tenants, 500),
              total_landlords: Math.max(propertiesRes.data.stats.total_landlords, 50)
            }));
          }
        } else {
          setProperties(FALLBACK_PROPERTIES);
        }

        // Set testimonials
        if (testimonialsRes?.data?.testimonials?.length > 0) {
          setTestimonials(testimonialsRes.data.testimonials);
        }
      } catch (error) {
        console.error('Error fetching landing page data:', error);
        setProperties(FALLBACK_PROPERTIES);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Auto-advance testimonials
  useEffect(() => {
    if (testimonials.length === 0) return;
    const timer = setInterval(nextTestimonial, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only load

  // Deep-link support: /pricing → scroll to the #pricing section
  const location = useLocation();
  useEffect(() => {
    // Map known path aliases to section anchors
    const PATH_TO_ANCHOR = {
      '/pricing': 'pricing',
      '/features': 'features',
      '/testimonials': 'testimonials',
      '/contact': 'contact',
    };
    const anchor = PATH_TO_ANCHOR[location.pathname] || (location.hash ? location.hash.slice(1) : null);
    if (!anchor) return;
    // Wait one tick for sections to mount
    const timer = setTimeout(() => {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  const fmtMoney = (amt) => `$${Number(amt || 0).toFixed(2).replace(/\.00$/, '')}`;
  const monthlyPrice = fmtMoney(plans?.monthly?.amount ?? 9.99);
  const annualPrice = fmtMoney(plans?.annual?.amount ?? 95);
  const annualBadgeText = plans?.annual?.badge || 'Best Value · Save 20%';
  const monthlyEquiv = plans?.annual?.amount ? fmtMoney(plans.annual.amount / 12) : '$7.92';
  const annualSavings = (plans?.monthly?.amount && plans?.annual?.amount)
    ? Math.max(0, Math.round(plans.monthly.amount * 12 - plans.annual.amount))
    : 25;
  const annualSavingsPct = (plans?.monthly?.amount && plans?.annual?.amount)
    ? Math.max(0, Math.round((1 - plans.annual.amount / (plans.monthly.amount * 12)) * 100))
    : 20;
  const planFeatures = plans?.features?.length ? plans.features : [
    'Unlimited properties & units',
    'Unlimited tenants & property managers',
    'Lease management & e-acknowledgment',
    'Payment tracking & receipts',
    'Documents & in-app messaging',
    'Reports & PDF exports',
    'Email & in-app notifications',
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="U-DORM" className="h-10 w-auto" />
              <span className="text-xl font-bold text-[#2F4F4F] dark:text-white">U-DORM</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <a href="#properties" className="text-sm font-medium hover:text-[#C85A42] transition-colors">{t('nav.properties')}</a>
              <a href="#features" className="text-sm font-medium hover:text-[#C85A42] transition-colors">{t('landing.features')}</a>
              <a href="#pricing" className="text-sm font-medium hover:text-[#C85A42] transition-colors" data-testid="nav-pricing">Pricing</a>
              <a href="#testimonials" className="text-sm font-medium hover:text-[#C85A42] transition-colors">{t('landing.testimonials')}</a>
              <a href="#contact" className="text-sm font-medium hover:text-[#C85A42] transition-colors">{t('landing.contactUs')}</a>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="minimal" />
              <Link to="/login">
                <Button variant="outline" size="sm">{t('auth.signIn')}</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-[#C85A42] hover:bg-[#B04A35]">{t('landing.getStarted')}</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24 bg-gradient-to-br from-[#2F4F4F] via-[#3a5f5f] to-[#2F4F4F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <Badge className="bg-[#C85A42]/20 text-[#C85A42] border-[#C85A42]/30 hover:bg-[#C85A42]/30">
                🏠 The #1 Dormitory Management Platform
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                {t('landing.heroTitle').split(' ').slice(0, 2).join(' ')}
                <span className="text-[#C85A42]"> {t('landing.heroTitle').split(' ').slice(2).join(' ')}</span>
              </h1>
              <p className="text-lg text-gray-300 max-w-xl">
                {t('landing.heroSubtitle')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-[#C85A42] hover:bg-[#B04A35] text-white">
                    {t('landing.startFreeTrial')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="#properties">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    {t('nav.properties')}
                  </Button>
                </a>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div className="text-center">
                  <p className="text-3xl font-bold">{stats.total_properties}+</p>
                  <p className="text-sm text-gray-400">{t('landing.properties')}</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold">{stats.total_tenants.toLocaleString()}+</p>
                  <p className="text-sm text-gray-400">{t('landing.tenants')}</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold">98%</p>
                  <p className="text-sm text-gray-400">{t('common.success')}</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=800&h=600&fit=crop" 
                  alt="Modern Dormitory"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-xl z-20">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{t('landing.trustedBy')}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.verified')} {t('landing.landlords')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties Section */}
      <section id="properties" className="py-16 lg:py-24 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4">Featured Properties</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Discover Your Perfect <span className="text-[#C85A42]">Living Space</span>
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Browse through our curated selection of verified dormitories and boarding houses worldwide
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {properties.map((property) => (
                <Card key={property.property_id} className="overflow-hidden hover:shadow-xl transition-shadow group">
                  <div className="relative">
                    <img 
                      src={property.image || "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=600&h=400&fit=crop"} 
                      alt={property.name}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-white/90 text-gray-900">
                        {property.available_units || 0} available
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-lg mb-1">{property.name}</h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                      <MapPin className="w-3 h-3" />
                      {property.city || property.address}, {property.province}
                    </p>
                    <p className="text-[#C85A42] font-bold mb-3">
                      {property.min_price && property.max_price ? (
                        `₱${property.min_price.toLocaleString()} - ₱${property.max_price.toLocaleString()}/mo`
                      ) : (
                        'Contact for pricing'
                      )}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {(property.amenities || []).slice(0, 3).map((amenity, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{amenity}</Badge>
                      ))}
                      {(property.amenities || []).length > 3 && (
                        <Badge variant="secondary" className="text-xs">+{property.amenities.length - 3}</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="text-center mt-10">
            <Link to="/register">
              <Button size="lg" variant="outline">
                View All Properties
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features for Landlords */}
      <section id="features" className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-[#2F4F4F]/10 text-[#2F4F4F] dark:bg-[#2F4F4F]/30 dark:text-[#7FBFBF]">For Landlords</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Manage Your Properties <span className="text-[#2F4F4F] dark:text-[#7FBFBF]">Like a Pro</span>
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                U-DORM gives landlords all the tools they need to efficiently manage their dormitories 
                and boarding houses, from tenant onboarding to financial reporting.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {LANDLORD_BENEFITS.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                    <div className="p-2 rounded-lg bg-[#2F4F4F]/10 dark:bg-[#2F4F4F]/30">
                      <benefit.icon className="w-5 h-5 text-[#2F4F4F] dark:text-[#7FBFBF]" />
                    </div>
                    <div>
                      <h4 className="font-medium mb-1">{benefit.title}</h4>
                      <p className="text-sm text-muted-foreground">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/register">
                  <Button size="lg" className="bg-[#2F4F4F] hover:bg-[#1F3F3F]">
                    Start as Landlord
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=600&h=500&fit=crop"
                alt="Landlord Dashboard"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <Wallet className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">₱1.2M</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Total Revenue Tracked</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features for Tenants */}
      <section className="py-16 lg:py-24 bg-[#C85A42]/5 dark:bg-[#C85A42]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <img 
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=500&fit=crop"
                alt="Happy Tenants"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                    <UserCheck className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">2,000+</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Happy Tenants</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <Badge className="mb-4 bg-[#C85A42]/10 text-[#C85A42]">For Tenants</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Your Rental Life, <span className="text-[#C85A42]">Simplified</span>
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                U-DORM makes it easy for tenants to find their perfect dorm, manage payments, 
                and communicate with landlords - all in one convenient app.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {TENANT_BENEFITS.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                    <div className="p-2 rounded-lg bg-[#C85A42]/10">
                      <benefit.icon className="w-5 h-5 text-[#C85A42]" />
                    </div>
                    <div>
                      <h4 className="font-medium mb-1">{benefit.title}</h4>
                      <p className="text-sm text-muted-foreground">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/register">
                  <Button size="lg" className="bg-[#C85A42] hover:bg-[#B04A35]">
                    Join as Tenant
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 lg:py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-white/10 text-white border-white/20">Testimonials</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              What Our Users <span className="text-[#C85A42]">Say</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Hear from landlords, property managers, and tenants who have transformed their rental experience with U-DORM
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <div className="bg-gray-800 rounded-2xl p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <img 
                  src={testimonials[currentTestimonial]?.image || "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"}
                  alt={testimonials[currentTestimonial]?.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-[#C85A42]"
                />
                <div className="text-center md:text-left flex-1">
                  <div className="flex justify-center md:justify-start gap-1 mb-4">
                    {[...Array(testimonials[currentTestimonial]?.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-lg text-gray-300 italic mb-6">
                    "{testimonials[currentTestimonial]?.text}"
                  </p>
                  <div>
                    <p className="font-semibold text-white" data-testid="testimonial-name">{testimonials[currentTestimonial]?.name}</p>
                    <p className="text-sm text-gray-400">
                      {testimonials[currentTestimonial]?.role} • {testimonials[currentTestimonial]?.location}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button 
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button 
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((t, index) => (
                <button
                  key={t.name || index}
                  data-testid={`testimonial-dot-${index}`}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentTestimonial ? 'bg-[#C85A42]' : 'bg-gray-600'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section id="pricing" className="py-16 lg:py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4">Simple Pricing</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              One Plan, <span className="text-[#C85A42]">Unlimited Everything</span>
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              No tiers, no tenant caps. Pay {monthlyPrice}/month, or pay annually and save ~{annualSavingsPct}%. No auto-renew.
            </p>
          </div>

          <div className="max-w-3xl mx-auto mb-8">
            <div className="flex items-start gap-3 rounded-xl border-2 border-[#6B8E6B] bg-[#6B8E6B]/10 p-4 sm:p-5" data-testid="free-until-notice">
              <Gift className="w-6 h-6 text-[#6B8E6B] shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-[#2F4F4F] dark:text-[#9ec49e]">Free for everyone until February 1, 2027 🎉</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                  Sign up and use the full platform free — no credit card required. Paid subscriptions begin on February 1, 2027; we'll remind you before then.
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Monthly */}
            <Card className="p-6 hover:shadow-lg transition-shadow" data-testid="landing-plan-monthly">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2">Monthly</h3>
                <p className="text-4xl font-bold text-[#2F4F4F] dark:text-[#7FBFBF]" data-testid="landing-monthly-price">
                  {monthlyPrice}<span className="text-sm font-normal text-muted-foreground">/mo</span>
                </p>
                <div className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800" data-testid="moneyback-badge-monthly">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  30-Day Money-Back Guarantee
                </div>
                <p className="text-sm text-muted-foreground mt-3 mb-6">No auto-renew · Pay only when you want</p>
                <ul className="text-sm space-y-3 text-left mb-6">
                  {planFeatures.map((f) => (
                    <li key={f} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {f}</li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button variant="outline" className="w-full">Get Started</Button>
                </Link>
              </div>
            </Card>

            {/* Annual - Best Value */}
            <Card className="p-6 border-2 border-[#C85A42] relative hover:shadow-lg transition-shadow" data-testid="landing-plan-annual">
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C85A42]">{annualBadgeText}</Badge>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2">Annual</h3>
                <p className="text-4xl font-bold text-[#C85A42]" data-testid="landing-annual-price">
                  {annualPrice}<span className="text-sm font-normal text-muted-foreground">/yr</span>
                </p>
                <div className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800" data-testid="moneyback-badge-annual">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  30-Day Money-Back Guarantee
                </div>
                <p className="text-sm text-muted-foreground mt-3 mb-6">~{monthlyEquiv}/mo — pay once, covered for the year</p>
                <ul className="text-sm space-y-3 text-left mb-6">
                  {planFeatures.map((f) => (
                    <li key={f} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {f}</li>
                  ))}
                  <li className="flex items-center gap-2 text-[#C85A42] font-medium"><CheckCircle className="w-4 h-4 text-[#C85A42]" /> Save ~${annualSavings}/year</li>
                </ul>
                <Link to="/register">
                  <Button className="w-full bg-[#C85A42] hover:bg-[#B04A35]">Get Started</Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 lg:py-24 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4">Contact Us</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Get in <span className="text-[#C85A42]">Touch</span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-10">
            Have questions about U-DORM? Want to partner with us? We'd love to hear from you.
            Send us a message on Messenger and we'll get back to you as soon as possible.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 max-w-xl mx-auto">
            {/* Message Us */}
            <a 
              href="https://m.me/mindfulux" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-3 p-6 rounded-2xl border bg-white dark:bg-gray-800 hover:shadow-lg transition-all hover:-translate-y-1 group"
              data-testid="contact-messenger-btn"
            >
              <div className="p-4 rounded-full bg-[#0084FF]/10 group-hover:bg-[#0084FF]/20 transition-colors">
                <svg className="w-7 h-7 text-[#0084FF]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.908 1.438 5.503 3.686 7.2V22l3.405-1.87c.91.252 1.875.388 2.909.388 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm1.066 12.441l-2.545-2.715-4.97 2.715 5.467-5.8 2.609 2.715 4.906-2.715-5.467 5.8z"/>
                </svg>
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">Message Us</span>
              <span className="text-sm text-muted-foreground">General inquiries & support</span>
            </a>

            {/* Free Demo */}
            <a 
              href="https://m.me/mindfulux?text=Hi%20MindfulUX!%20I'd%20like%20a%20free%20demo%20of%20U-DORM." 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-3 p-6 rounded-2xl border-2 border-[#C85A42] bg-[#C85A42]/5 hover:bg-[#C85A42]/10 hover:shadow-lg transition-all hover:-translate-y-1 group"
              data-testid="contact-free-demo-btn"
            >
              <div className="p-4 rounded-full bg-[#C85A42]/10 group-hover:bg-[#C85A42]/20 transition-colors">
                <Play className="w-7 h-7 text-[#C85A42]" />
              </div>
              <span className="font-semibold text-[#C85A42]">FREE DEMO</span>
              <span className="text-sm text-muted-foreground">Message us for a free demo</span>
            </a>
          </div>

          <p className="mt-8 text-sm text-muted-foreground">
            Prefer email? Reach us at{' '}
            <a href="mailto:info@mindfulux.org" className="text-[#C85A42] hover:underline" data-testid="contact-email-link">
              info@mindfulux.org
            </a>
            . We typically respond within a few hours during business hours.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-[#2F4F4F] to-[#3a5f5f] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Transform Your Dorm Management?
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Join thousands of landlords and tenants who have simplified their rental experience with U-DORM.
            Get started in minutes — backed by our 30-day money-back guarantee.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-[#C85A42] hover:bg-[#B04A35] text-white">
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="https://m.me/mindfulux" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Contact Sales
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="U-DORM" className="h-8 w-auto" />
                <span className="text-xl font-bold text-white">U-DORM</span>
              </div>
              <p className="text-sm">
                The leading dormitory and boarding house management platform.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors" data-testid="footer-pricing">Pricing</a></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Sign In</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Sign Up</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/cookie-policy" className="hover:text-white transition-colors">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>© {new Date().getFullYear()} MindfulUX. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
